import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import Button from "@mui/material/Button";
import "./HistoryOffer.css";

const formatDateToRomanian = (dateString) => {
  if (!dateString || dateString === "") return "";

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const formatAmountRO = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim();
  const match = raw.match(/-?[\d.,\s]+/);
  if (!match) return raw;

  const numericPart = match[0].trim();
  let normalized = numericPart.replace(/\s/g, "");

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  const n = Number(normalized);
  if (Number.isNaN(n)) return raw;

  const formatted = new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  const suffix = raw.replace(match[0], "").trim();
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const formatExchangeRateRO = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim();
  const match = raw.match(/-?[\d.,\s]+/);
  if (!match) return raw;

  const numericPart = match[0].trim();
  let normalized = numericPart.replace(/\s/g, "");

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  const n = Number(normalized);
  if (Number.isNaN(n)) return raw;

  const formatted = new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(n);

  const suffix = raw.replace(match[0], "").trim();
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const HistoryOffer = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [revisions, setRevisions] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const RAW_API_BASE =
    process.env.REACT_APP_API_URL ||
    axiosInstance.defaults.baseURL ||
    window.location.origin;

  const API_BASE_URL = String(RAW_API_BASE)
    .replace(/\/api\/?$/, "")
    .replace(/\/+$/, "");

  const fetchRevisionHistory = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await axiosInstance.get(
        `/offer-register/${id}/revisions/`
      );

      const items = response.data || [];
      setRevisions(items);

      const currentRevisionIndex = items.findIndex(
        (item) => item.is_latest_revision === true
      );

      setTabIndex(currentRevisionIndex >= 0 ? currentRevisionIndex : 0);
    } catch (error) {
      console.error("Error fetching revision history:", error);
      setErrorMessage("Failed to fetch revision history.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRevisionHistory();
  }, [fetchRevisionHistory]);

  const activeRevision = useMemo(() => {
    if (!revisions.length) return null;
    return revisions[tabIndex] || null;
  }, [revisions, tabIndex]);

  const matchPageProtocol = (url) => {
    if (!url) return url;

    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      return url.replace(/^http:\/\//i, "https://");
    }

    return url;
  };

  const getFileUrl = (file) => {
    if (!file) return null;

    if (file.file_url) return matchPageProtocol(file.file_url);
    if (file.url) return matchPageProtocol(file.url);

    if (file.file) {
      const rawPath = String(file.file);

      if (/^https?:\/\//i.test(rawPath)) {
        return matchPageProtocol(rawPath);
      }

      const normalizedPath = rawPath.startsWith("/")
        ? rawPath
        : `/media/${rawPath.replace(/^media\//, "")}`;

      return matchPageProtocol(`${API_BASE_URL}${normalizedPath}`);
    }

    return null;
  };

  const getExistingFileName = (file) => {
    if (file?.filename) return file.filename;

    if (typeof file?.file === "string") {
      const parts = file.file.split("/");
      return parts[parts.length - 1];
    }

    return "download";
  };

  const isPdfExistingFile = (file) => {
    const filename = getExistingFileName(file).toLowerCase();
    return filename.endsWith(".pdf");
  };

  const getFileIcon = (file) => {
    const filename = file?.filename || file?.file?.name || file?.file || "";
    const extension = filename.split(".").pop()?.toLowerCase() || "";

    if (extension === "pdf") {
      return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
    }

    if (["doc", "docx"].includes(extension)) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
    }

    if (["xls", "xlsx"].includes(extension)) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
    }

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
    }

    return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
  };

  const handleExportRevisionDocx = useCallback(async (revision) => {
    if (!revision?.id) return;

    try {
      const response = await axiosInstance.get(
        `/offer-register/${revision.id}/export-docx/`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const safeCode = String(revision.offer_code || revision.id).replace(
        /[\\/]/g,
        "_"
      );

      const revisionSuffix =
        revision.revision_number === 0
          ? "original"
          : `rev_${revision.revision_number}`;

      link.href = url;
      link.setAttribute(
        "download",
        `oferta_${safeCode}_${revisionSuffix}.docx`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting revision DOCX:", error);
      setErrorMessage(
        error?.response?.data?.detail ||
        "Failed to export the offer in DOCX format."
      );
    }
  }, []);

  const handleFileClick = async (file) => {
    const fileUrl = getFileUrl(file);

    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const filename = getExistingFileName(file);

      if (isPdfExistingFile(file)) {
        const newWindow = window.open();

        if (!newWindow) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        newWindow.document.write(`
          <html>
            <head>
              <title>${filename}</title>
              <style>
                body { margin: 0; height: 100vh; }
                embed { width: 100%; height: 100%; }
              </style>
            </head>
            <body>
              <embed src="${objectUrl}" type="application/pdf" />
            </body>
          </html>
        `);

        newWindow.document.close();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } else {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (error) {
      console.error("Error opening attachment:", error);
    }
  };

  if (loading) {
    return (
      <div className="history-offer-page">
        <div className="history-offer-loading">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="history-offer-page">
        <Alert severity="error">{errorMessage}</Alert>
      </div>
    );
  }

  if (!activeRevision) {
    return (
      <div className="history-offer-page">
        <Alert severity="info">No revision history available.</Alert>
      </div>
    );
  }

  return (
    <div className="history-offer-page">
      <div className="history-offer-header">
        <div className="history-offer-header-left">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />

          <div className="history-offer-title-wrap">
            <h2 className="history-offer-title">REVISION HISTORY</h2>
            <p className="history-offer-subtitle">
              <span className="history-offer-subtitle-icon">✨</span>
              <span className="history-offer-subtitle-text">
                All revisions for the selected offer, including the current one.
              </span>
            </p>
          </div>
        </div>
      </div>

      <Divider className="history-offer-header-divider" />

      <Paper className="history-offer-tabs-card" elevation={0}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className="history-offer-tabs"
        >
          {revisions.map((revision) => (
            <Tab
              key={revision.id}
              label={
                revision.revision_number === 0
                  ? "Original"
                  : `Rev ${revision.revision_number}`
              }
              className="history-offer-tab"
            />
          ))}
        </Tabs>
      </Paper>

      <Paper className="history-offer-content-card" elevation={0}>
        <div className="history-offer-top">
          <div className="history-offer-top-left">
            <Typography variant="h6" className="history-offer-code">
              {activeRevision.offer_code || "-"}
            </Typography>
          </div>

          <div className="history-offer-top-right">
            <Button
              onClick={() => handleExportRevisionDocx(activeRevision)}
              startIcon={<DownloadIcon />}
              className="export-docx-btn"
              disableElevation
            >
              Export DOCX
            </Button>
            <Typography
              variant="body2"
              className={`history-offer-current-tag ${activeRevision.is_latest_revision
                ? "history-offer-current-tag--current"
                : "history-offer-current-tag--historical"
                }`}
            >
              {activeRevision.is_latest_revision
                ? "Current revision"
                : "Historical revision"}
            </Typography>
          </div>
        </div>

        <Divider className="history-offer-divider" />

        <Box className="history-offer-grid">
          <div className="history-offer-card">
            <span className="history-offer-label">Offer Date</span>
            <span className="history-offer-value">
              {formatDateToRomanian(activeRevision.offer_date)}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Client</span>
            <span className="history-offer-value">
              {activeRevision.client_name || "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Contact Person</span>
            <span className="history-offer-value">
              {(activeRevision.contact_persons_details || []).length > 0
                ? activeRevision.contact_persons_details
                  .map((contact) =>
                    `${contact.name}${contact.position ? ` - ${contact.position}` : ""}`
                  )
                  .join(", ")
                : "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Offer Object</span>
            <span className="history-offer-value">
              {activeRevision.offer_object || "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Execution Term</span>
            <span className="history-offer-value">
              {activeRevision.execution_term || "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Status</span>
            <span className="history-offer-value">
              {activeRevision.status || "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Validity</span>
            <span className="history-offer-value">
              {activeRevision.valability
                ? `${activeRevision.valability} days`
                : "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Total</span>
            <span className="history-offer-value">
              {formatAmountRO(activeRevision.total_display || "-")}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">System Total</span>
            <span className="history-offer-value">
              {formatAmountRO(activeRevision.total_system_display || "-")}
            </span>
          </div>

          <div className="history-offer-card history-offer-card-full">
            <span className="history-offer-label">Description</span>
            <span className="history-offer-value">
              {activeRevision.description || "-"}
            </span>
          </div>
        </Box>

        <div className="history-offer-section">
          <h3>Offer Opportunities</h3>

          <div className="history-offer-table-wrapper">
            <table className="history-offer-table">
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Project Code</th>
                  <th>Subproject Code</th>
                  <th>Currency</th>
                  <th>Exchange Rate</th>
                  <th className="col-sum">Value (Original currency)</th>
                  <th className="col-sum">Value</th>
                </tr>
              </thead>

              <tbody>
                {(activeRevision.offer_opportunities || []).length > 0 ? (
                  activeRevision.offer_opportunities.map((row) => (
                    <tr key={row.id}>
                      <td>{row.opportunity_label || "-"}</td>
                      <td>{row.project_code_label || "-"}</td>
                      <td>{row.subproject_code_label || "-"}</td>
                      <td>{row.currency_label || "-"}</td>
                      <td>
                        {formatExchangeRateRO(row.exchange_rate_value || "-")}
                      </td>
                      <td>{formatAmountRO(row.sum_display || "-")}</td>
                      <td>
                        {formatAmountRO(
                          row.sum_system_currency_display || "-"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="history-offer-empty-row">
                      No opportunities available for this revision.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="history-offer-section">
          <h3>Additional Fields</h3>

          {(activeRevision.custom_fields || []).length > 0 ? (
            <Box className="history-offer-grid">
              {activeRevision.custom_fields.map((field, index) => (
                <div key={field.id || index} className="history-offer-card">
                  <span className="history-offer-label">
                    {field.field_title || "-"}
                  </span>
                  <span className="history-offer-value">
                    {field.field_content || "-"}
                  </span>
                </div>
              ))}
            </Box>
          ) : (
            <div className="history-offer-empty-files">
              No additional fields available for this revision.
            </div>
          )}
        </div>

        <div className="history-offer-section">
          <h3>Attachments</h3>

          {(activeRevision.attach_files || []).length > 0 ? (
            <div className="history-offer-files-list">
              {activeRevision.attach_files.map((file, index) => (
                <div
                  key={file.id || index}
                  className="history-offer-file-item"
                >
                  <div className="history-offer-file-info">
                    {getFileIcon(file)}

                    <span
                      className="history-offer-file-name clickable"
                      title={
                        isPdfExistingFile(file)
                          ? "Click to preview PDF"
                          : "Click to download file"
                      }
                      onClick={() => handleFileClick(file)}
                    >
                      {getExistingFileName(file)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="history-offer-empty-files">
              No attachments available for this revision.
            </div>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default HistoryOffer;